import { Card, CardContent } from "@/components/ui/card"
import { Button, type buttonVariants } from "@/components/ui/button"
import { Check, Info } from "lucide-react"
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip"
import { Tagline } from "@/components/pro-blocks/landing-page/tagline"
import type { VariantProps } from "class-variance-authority"

const pricingData = {
  plans: [
    {
      name: "Starter",
      description: "Для небольших команд с умеренным потоком обращений",
      features: [
        { name: "До 500 обращений/месяц", tooltip: "Лимит обрабатываемых обращений" },
        { name: "Автоматическая категоризация", tooltip: "ИИ определяет тип каждого обращения" },
        { name: "Еженедельные отчёты", tooltip: "Дайджест по email каждый понедельник" },
        { name: "1 система поддержки", tooltip: "Zendesk, Intercom или Help Scout" },
      ],
      price: 199,
      period: "/месяц",
      variant: "outline",
    },
    {
      name: "Professional",
      description: "Для растущих компаний с активной поддержкой",
      features: [
        { name: "До 2,000 обращений/месяц", tooltip: "Расширенный лимит обращений" },
        { name: "Настраиваемые категории", tooltip: "Адаптация под ваш продукт" },
        { name: "Мгновенные уведомления", tooltip: "Алерты о критических проблемах" },
        { name: "Все системы поддержки", tooltip: "Подключайте несколько источников" },
        { name: "Интеграция с трекерами", tooltip: "Jira, Linear, Asana и др." },
      ],
      price: 349,
      period: "/месяц",
      variant: "default",
      highlighted: true,
    },
    {
      name: "Enterprise",
      description: "Для крупных компаний с особыми требованиями",
      features: [
        { name: "Неограниченные обращения", tooltip: "Без лимитов" },
        { name: "Настройка под продукт", tooltip: "Индивидуальная адаптация ИИ" },
        { name: "Выделенный менеджер", tooltip: "Персональная поддержка" },
        { name: "Расширенная безопасность", tooltip: "SOC 2, SSO, audit logs" },
        { name: "Обучение команды", tooltip: "Онбординг и тренинги" },
      ],
      price: 499,
      period: "+/месяц",
      variant: "outline",
    },
  ],
}

export function PricingSection3() {
  return (
    <section className="bg-secondary section-padding-y border-b" aria-labelledby="pricing-section-title-3" id="pricing">
      <div className="container-padding-x container mx-auto">
        <div className="flex flex-col items-center gap-10 md:gap-12">
          <div className="section-title-gap-lg flex max-w-xl flex-col items-center text-center">
            <Tagline>Тарифы</Tagline>
            <h2 id="pricing-section-title-3" className="heading-lg text-foreground">
              Выберите свой план
            </h2>
            <p className="text-muted-foreground">Начните бесплатно. Подключение за 5 минут. Отмена в любой момент.</p>
          </div>

          <div className="grid w-full max-w-5xl gap-4 md:grid-cols-3">
            {pricingData.plans.map((plan) => (
              <Card
                key={plan.name}
                className={`p-6 shadow-none ${
                  plan.highlighted ? "border-primary shadow-[0px_0px_0px_4px_rgba(249,94,22,0.1)]" : ""
                }`}
              >
                <CardContent className="flex flex-col gap-6 p-0">
                  <div className="flex flex-col gap-3">
                    <h3 className={`text-lg font-semibold ${plan.highlighted ? "text-primary" : ""}`}>{plan.name}</h3>
                    <p className="text-muted-foreground text-sm">{plan.description}</p>
                  </div>

                  <div className="flex items-end gap-0.5">
                    <span className="text-4xl font-semibold">${plan.price}</span>
                    <span className="text-muted-foreground text-base">{plan.period}</span>
                  </div>

                  <Button variant={plan.variant as VariantProps<typeof buttonVariants>["variant"]} className="w-full">
                    {plan.name === "Enterprise" ? "Связаться с нами" : "Начать бесплатно"}
                  </Button>

                  <div className="flex flex-col gap-3">
                    {plan.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <Check className="text-primary h-5 w-5 shrink-0" />
                        <span className="text-muted-foreground flex-1 text-sm">{feature.name}</span>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <Info className="text-muted-foreground h-4 w-4 cursor-pointer opacity-70 hover:opacity-100" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p>{feature.tooltip}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* ROI callout */}
          <Card className="bg-primary/5 w-full max-w-2xl border-none p-6 text-center shadow-none">
            <h4 className="text-foreground mb-2 font-semibold">Окупаемость в первый месяц</h4>
            <p className="text-muted-foreground text-sm">
              Платите $349/мес — экономите $2,400/мес на времени продакта + предотвращаете отток на $10,000+
            </p>
            <p className="text-primary mt-2 font-semibold">ROI: 8x</p>
          </Card>
        </div>
      </div>
    </section>
  )
}
